import {store} from '../store';
import {Feature, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {type IOneClickUpsellProducts} from '../models/IOneClickUpsellProducts';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import spinnerImageURL from '../../img/spinner.svg';
import {addProductToCart} from '../util/cart';
import {renderOrderNotice, requestCartCalculation} from '../payment/order';
import {getLocaleText} from '../util/translation';

export function initOneClickUpsell() {
	if (!Feature.enabled(FeatureFlag.ONE_CLICK_UPSELL)) {
		return;
	}

	const ocuFlow = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'pp_ocu_flow');

	if (ocuFlow === 'pp_button') {
		store.subscribe(() => {
			if ($qs('#pp-ocu-container')?.getAttribute('aria-expanded') === 'false') {
				displayOCUPage();
			}
		});
	} else if (ocuFlow === 'before_payment') {
		$qsAll('#pp-continue-to-payment, #pp-continue-to-payment-mobile', element => {
			element.addEventListener('click', () => {
				store.subscribe(() => {
					if ($qs('#pp-ocu-container')?.getAttribute('aria-expanded') === 'false') {
						displayOCUPage();
					}
				});
			});
		});
	}
}

/**
 * UI for displaying the OCU page.
 */
export function displayOCUPage() {
	const ocuHeadline = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'headline_text');
	const ocuSubHeadline = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'sub_headline_text');
	const acceptBtnText = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'accept_button_text');
	const declineBtnText = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'decline_button_text');
	const customDesc = Feature.metadata<string>(FeatureFlag.ONE_CLICK_UPSELL, 'custom_description');
	const ocuProducts = Feature.dynamicMetadata<IOneClickUpsellProducts>(FeatureFlag.ONE_CLICK_UPSELL, 'pp_ocu_products');

	$qsAll('#pp-modal-content, #pp-related-products-section', $el => {
		$el.style.zIndex = '-1';
	});
	renderOCUPage(ocuHeadline, ocuSubHeadline, customDesc, acceptBtnText, declineBtnText, ocuProducts);
	initOCUEvents();
}

/**
 * For rendering the One-Click-Upsell Page as a separate page from the checkout modal
 * @param ocuHeadline Headline text
 * @param subHeadline Sub-headline text
 * @param acceptBtnText Aceept button text
 * @param declineBtnText Decline button text
 * @param ocuProducts One-Click-Upsell product data
 */
// eslint-disable-next-line max-params
export function renderOCUPage(
	ocuHeadline: string | null,
	subHeadline: string | null,
	customDesc: string | null,
	acceptBtnText: string | null,
	declineBtnText: string | null,
	ocuProducts: IOneClickUpsellProducts | null,
) {
	clearOCUSection();
	if (ocuProducts && ocuHeadline && ocuProducts && acceptBtnText && declineBtnText) {
		const ocuContainer = $qs('#pp-ocu-container');
		ocuContainer?.classList.remove('hide');
		ocuContainer?.setAttribute('aria-expanded', 'true');
		const ocuBody = document.createElement('div');
		ocuBody.className = 'pp-ocu-body';
		ocuBody.innerHTML = `
                <button class="pp-ocu-close">&times;</button>
                <span class="pp-ocu-headline">${ocuHeadline}</span>
                <div class="pp-ocu-sub-headline ${subHeadline ? '' : 'hide'}">
                    <span>${subHeadline ?? ''}</span>
                </div>
                <div class="pp-ocu-contents">
                    <img class="pp-ocu-product-img" src=${ocuProducts.image as unknown as string}>
                    <span class="pp-ocu-product-name">${ocuProducts.name}</span>
                    <span class="pp-ocu-product-description ${customDesc ? '' : 'hide'}">${customDesc ?? ''}</span>
                    <div class="pp-ocu-product-price">${ocuProducts.price}</div>
                </div>
                <button class="pp-ocu-accept-button" data-ocu_id=${ocuProducts.id}>${acceptBtnText}</button>
                <button class="pp-ocu-decline-button">${declineBtnText}</button>`;
		ocuContainer?.prepend(ocuBody);
	}
}

/**
 * UI for clearing the One-Click-Upsell section after each refresh
 */
function clearOCUSection() {
	for (const ocuProduct of $qsAll('.pp-ocu-body')) {
		ocuProduct.remove();
	}
}

/**
 * For setting up the UI of closing OCU page and adding OCU product to cart.
 */
function initOCUEvents() {
	for (const ocuClose of $qsAll('.pp-ocu-close, .pp-ocu-decline-button')) {
		ocuClose.addEventListener('click', () => {
			closeOCUPage();
		});
	}

	$qs('.pp-ocu-accept-button')?.addEventListener('click', async event => {
		const $target = event.target as HTMLButtonElement;
		const productId = Number($target.dataset['pid']);

		if (!productId || Number.isNaN(productId)) {
			renderOrderNotice(getLocaleText('Something went wrong. Please refresh the page and try again.'));
			return;
		}

		store.dispatch(startModalLoading());

		$target.disabled = true;
		const markup = $target.innerHTML;
		$target.innerHTML = `<img src="${spinnerImageURL}" class="ocu-spinner"/> Updating your order ...`;

		await addProductToCart(productId);

		$target.disabled = false;
		$target.innerHTML = markup;

		await requestCartCalculation();
		store.dispatch(stopModalLoading());
	});
}

/**
 * UI for closing the OCU page.
 */
export function closeOCUPage() {
	$qs('#pp-ocu-container')?.classList.add('pp-blowup-close');
	setTimeout(() => {
		$qs('#pp-ocu-container', $el => {
			$el.style.backgroundColor = 'unset';
		});
		$qsAll('#pp-modal-content, #pp-related-products-section', $el => {
			$el.style.zIndex = '1';
		});
		$qs('#pp-ocu-container')?.classList.add('hide');
	}, 500);
}
